import { getTopologyType } from '_store/application/hooks';
import { DenmSourceFilter, DenmStatusFilter } from '_store/denm/denmsTypes';
import { IvimSourceFilter, IvimStatusFilter } from '_store/ivim/ivimsTypes';
import { pollVmsStatus } from '_store/pmv/sagas';
import { PayloadAction } from '@reduxjs/toolkit';
import { createSliceSaga, SagaType } from 'redux-toolkit-saga';
import { all, call, put, takeLatest } from 'typed-redux-saga';

import { getFromLocalStorage } from '../../../utils';
import applicationReducer from '../../application/reducer';
import { TopologyType } from '../../application/types';
import denmsReducer from '../../denm/denmsReducers';
import devicesReducer from '../../devices/devicesReducers';
import ivimsReducer from '../../ivim/ivimsReducers';
import pmvsReducer from '../../pmv/pmvsReducers';
import roadsReducer from '../../roads/roadsReducers';
import { roadsRequest } from '../../roads/sagas';
import settingsReducer from '../../settings/settingsReducers';
import spatemsReducer from '../../spatem/spatemsReducers';
import apiReducer from '../reducer';
import {
  pollTaskWatcherCAM,
  pollTaskWatcherDENM,
  pollTaskWatcherIVIM,
  pollTaskWatcherRSU,
  pollTaskWatcherSPATEM,
} from './webSocket';

function* createClientFromCredentials(_action: PayloadAction<{ token: string }>) {
  const { token } = _action.payload;
  window.sessionStorage.setItem('token', token);
  const roadsResponse = yield call(roadsRequest, null);
  const selectedRoadsFromLocalStorage = yield call(getFromLocalStorage, 'selectedRoadIds');
  const roadSegmentIds: string[] = selectedRoadsFromLocalStorage
    ? JSON.parse(selectedRoadsFromLocalStorage)
    : [];
  const roadSegments =
    roadSegmentIds && roadSegmentIds.length > 0
      ? roadSegmentIds
      : roadsResponse?.results.map((road) => road.id);

  const topologyType = getTopologyType();
  const defaultDenmSources =
    topologyType === TopologyType.URBAN
      ? [
          DenmSourceFilter.EMERAS,
          DenmSourceFilter.CONNEX,
          DenmSourceFilter.DATEXII,
          DenmSourceFilter.TMS_UPER,
        ]
      : [DenmSourceFilter.EMERAS, DenmSourceFilter.CONNEX, DenmSourceFilter.DATEXII];

  const defaultIvimSources =
    topologyType === TopologyType.URBAN
      ? [
          IvimSourceFilter.EMERAS,
          IvimSourceFilter.DATEXII,
          IvimSourceFilter.DENM,
          IvimSourceFilter.TMS_UPER,
        ]
      : [IvimSourceFilter.EMERAS, IvimSourceFilter.DATEXII, IvimSourceFilter.DENM];
  try {
    yield put(settingsReducer.actions.settingsRequest({}));
    yield all([
      put(denmsReducer.actions.causesRequest()),
      put(denmsReducer.actions.subCausesRequest()),
    ]);
    if (roadSegments) {
      yield put(devicesReducer.actions.devicesRequest({ roadSegmentIds: roadSegments }));
      yield put(pmvsReducer.actions.vmsRequest());
      // Only request SPATEMs if topology is URBAN
      if (topologyType === TopologyType.URBAN) {
        yield put(spatemsReducer.actions.spatemsRequest({ roadSegmentIds: roadSegments }));
      }
      yield put(
        denmsReducer.actions.denmsRequest({
          status: DenmStatusFilter.IN_PROGRESS,
          roadSegmentIds: roadSegments,
          sources: defaultDenmSources,
        }),
      );
      yield put(
        ivimsReducer.actions.ivimsRequest({
          roadSegmentIds: roadSegments,
          status: IvimStatusFilter.IN_PROGRESS,
          sources: defaultIvimSources,
        }),
      );
      yield put(roadsReducer.actions.selectRoad(roadSegments));
    }
    yield put(applicationReducer.actions.readyStatusReached());
    yield put(applicationReducer.actions.setUserLoggedIn());
    yield all([
      call(pollTaskWatcherDENM, token),
      call(pollTaskWatcherIVIM, token),
      call(pollTaskWatcherRSU, token),
      call(pollTaskWatcherCAM, token),
      call(pollVmsStatus),
      // Only initialize SPATEM WebSocket if topology is URBAN
      ...(topologyType === TopologyType.URBAN ? [call(pollTaskWatcherSPATEM, token)] : []),
    ]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('createClientFromCredentials ERROR >>>>>>>>>>> ', e);
  }
}

function* renewToken(_action: PayloadAction<{ token: string }>) {
  const { token } = _action.payload;
  window.sessionStorage.setItem('token', token);
  const topologyType = getTopologyType();
  try {
    yield all([
      call(pollTaskWatcherDENM, token),
      call(pollTaskWatcherIVIM, token),
      call(pollTaskWatcherRSU, token),
      call(pollTaskWatcherCAM, token),
      call(pollVmsStatus),
      // Only initialize SPATEM WebSocket if topology is URBAN
      ...(topologyType === TopologyType.URBAN ? [call(pollTaskWatcherSPATEM, token)] : []),
    ]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('renewal token ERROR >>>>>>>>>>> ', e);
  }
}

// Monitor for WebSocket token expiration
function* handleWebsocketTokenExpired() {
  try {
    // Verifica se c'è ancora un utente autenticato
    const currentToken = window.sessionStorage.getItem('token');

    if (!currentToken) {
      console.log("Nessun token trovato, l'utente non è autenticato");
      // Se non c'è token, forza un reload per il re-login
      window.location.reload();
      return;
    }

    // Mostra un messaggio informativo invece di errore
    yield put(
      applicationReducer.actions.toastAdded({
        id: 'websocket-token-expired',
      }),
    );

    // Attendi un momento prima di forzare il reload per permettere all'utente di vedere il messaggio
    yield new Promise((resolve) => setTimeout(resolve, 2000));

    // Forza il reload della pagina per far riapparire il login
    window.location.reload();
  } catch (error) {
    console.error('Errore durante la gestione del token scaduto:', error);
    // In caso di errore, forza comunque il reload
    window.location.reload();
  } finally {
    yield put(apiReducer.actions.websocketTokenRefreshed());
  }
}

export const apiSagas = createSliceSaga({
  name: 'api',
  caseSagas: {
    createClientFromCredentials,
    renewToken,
  },
  sagaType: SagaType.Watch,
});

// Aggiungi un watcher per l'evento di token scaduto
export function* apiSagasWatcher() {
  console.log('🔥 apiSagasWatcher started');
  yield takeLatest(apiReducer.actions.websocketTokenExpired.type, handleWebsocketTokenExpired);
}

export default apiSagas;
